import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, CircularProgress, Collapse, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails,
  Tooltip, Checkbox, FormControlLabel, Alert,
  List, ListItem, ListItemIcon, ListItemText,
  alpha, useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StraightenIcon from '@mui/icons-material/Straighten';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NavigationIcon from '@mui/icons-material/Navigation';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ImageIcon from '@mui/icons-material/Image';
import VerifiedIcon from '@mui/icons-material/Verified';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import { useDropzone } from 'react-dropzone';
import AssessmentIcon from '@mui/icons-material/Assessment';
import api from '../../../services/api';
import AuthenticatedImage from '../../common/AuthenticatedImage';
import { useCesiumViewer } from '../../../context/CesiumViewerContext';
import { useAuth } from '../../../context/AuthContext';
import SlantingReport from './SlantingReport';
import * as Cesium from 'cesium';

const DEEP_PURPLE = '#7c4dff';

// ─── Tilt interpretation bands (matches API spec Section 8) ───
const TILT_BANDS = [
  { min: 0, max: 0.5, label: 'Essentially Vertical', key: 'essentially_vertical', color: '#4caf50', icon: CheckCircleIcon },
  { min: 0.5, max: 2.0, label: 'Minor Tilt (Monitor)', key: 'minor_tilt', color: '#8bc34a', icon: InfoIcon },
  { min: 2.0, max: 5.0, label: 'Notable Tilt (Investigate)', key: 'notable_tilt', color: '#ff9800', icon: WarningAmberIcon },
  { min: 5.0, max: Infinity, label: 'Significant Tilt (Immediate)', key: 'significant_tilt', color: '#f44336', icon: ErrorIcon },
];

// ─── Processing pipeline stages (matches API spec Section 3.3) ───
const PROCESSING_STAGES = [
  { name: 'preprocessing', label: 'Preparing images and extracting metadata' },
  { name: 'segmentation', label: 'Running AI pole segmentation' },
  { name: 'edge_detection', label: 'Detecting edge points' },
  { name: 'line_fitting', label: 'Fitting profile lines (RANSAC)' },
  { name: 'tilt_computation', label: 'Computing tilt angles' },
  { name: 'report_generation', label: 'Generating assessment and annotations' },
];

// ─── Frame visualization tabs ───
const FRAME_STAGES = [
  { key: 'raw', label: 'Raw Frame' },
  { key: 'segmentation', label: 'Segmentation' },
  { key: 'edge_points', label: 'Edge Points' },
  { key: 'profile_lines', label: 'Profile Lines' },
  { key: 'tilt_result', label: 'Tilt Result' },
  { key: 'geometry', label: 'All Layers' },
];

// ─── Warning severity config ───
const SEVERITY_CONFIG = {
  error: { color: '#f44336', icon: ErrorIcon },
  warning: { color: '#ff9800', icon: WarningAmberIcon },
  info: { color: '#2196f3', icon: InfoIcon },
};

const getTiltBand = (angle) => {
  const absAngle = Math.abs(angle);
  return TILT_BANDS.find(b => absAngle >= b.min && absAngle < b.max) || TILT_BANDS[TILT_BANDS.length - 1];
};

const getDirectionLabel = (degrees) => {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return dirs[index];
};

// ─── SVG Annotation Overlay Component ───
const AnnotationOverlay = ({ annotations, stageKey, width, height }) => {
  if (!annotations) return null;

  const { image_dimensions } = annotations;
  if (!image_dimensions) return null;

  const vw = image_dimensions.width;
  const vh = image_dimensions.height;

  const showSegmentation = stageKey === 'segmentation' || stageKey === 'geometry';
  const showEdgePoints = stageKey === 'edge_points' || stageKey === 'geometry';
  const showProfileLines = stageKey === 'profile_lines' || stageKey === 'geometry';
  const showTiltResult = stageKey === 'tilt_result' || stageKey === 'geometry';

  return (
    <svg
      viewBox={`0 0 ${vw} ${vh}`}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Segmentation polygon */}
      {showSegmentation && annotations.segmentation_polygon && (
        <polygon
          points={annotations.segmentation_polygon.map(p => p.join(',')).join(' ')}
          fill="rgba(124, 77, 255, 0.2)"
          stroke={DEEP_PURPLE}
          strokeWidth={8}
        />
      )}

      {/* Edge points */}
      {showEdgePoints && annotations.left_edge_points && annotations.left_edge_points.map((p, i) => (
        <circle key={`le-${i}`} cx={p[0]} cy={p[1]} r={18} fill="#4caf50" stroke="#fff" strokeWidth={4} />
      ))}
      {showEdgePoints && annotations.right_edge_points && annotations.right_edge_points.map((p, i) => (
        <circle key={`re-${i}`} cx={p[0]} cy={p[1]} r={18} fill="#ff9800" stroke="#fff" strokeWidth={4} />
      ))}

      {/* Profile lines */}
      {showProfileLines && annotations.left_profile_line && (
        <line
          x1={annotations.left_profile_line.x1} y1={annotations.left_profile_line.y1}
          x2={annotations.left_profile_line.x2} y2={annotations.left_profile_line.y2}
          stroke="#4caf50" strokeWidth={8} strokeDasharray="30,15"
        />
      )}
      {showProfileLines && annotations.right_profile_line && (
        <line
          x1={annotations.right_profile_line.x1} y1={annotations.right_profile_line.y1}
          x2={annotations.right_profile_line.x2} y2={annotations.right_profile_line.y2}
          stroke="#ff9800" strokeWidth={8} strokeDasharray="30,15"
        />
      )}

      {/* Tilt result: center line + vertical reference + angle arc */}
      {showTiltResult && annotations.center_line && (
        <line
          x1={annotations.center_line.x1} y1={annotations.center_line.y1}
          x2={annotations.center_line.x2} y2={annotations.center_line.y2}
          stroke={DEEP_PURPLE} strokeWidth={10}
        />
      )}
      {showTiltResult && annotations.vertical_reference_line && (
        <line
          x1={annotations.vertical_reference_line.x1} y1={annotations.vertical_reference_line.y1}
          x2={annotations.vertical_reference_line.x2} y2={annotations.vertical_reference_line.y2}
          stroke="#fff" strokeWidth={6} strokeDasharray="20,10" opacity={0.7}
        />
      )}
      {showTiltResult && annotations.tilt_angle_indicator && (
        <>
          <circle
            cx={annotations.tilt_angle_indicator.vertex[0]}
            cy={annotations.tilt_angle_indicator.vertex[1]}
            r={20} fill={DEEP_PURPLE} stroke="#fff" strokeWidth={4}
          />
          <text
            x={annotations.tilt_angle_indicator.vertex[0] + 40}
            y={annotations.tilt_angle_indicator.vertex[1] + vw * 0.025 + 20}
            fill="#fff" fontSize={vw * 0.025} fontWeight="bold"
            stroke="#000" strokeWidth={6} paintOrder="stroke"
          >
            {annotations.tilt_angle_indicator.angle_degrees.toFixed(1)}°
          </text>
        </>
      )}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const SlantingAnalysisModal = ({ open, onClose, asset, onDataMutated, onShow3DChange }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const viewerRef = useCesiumViewer();
  const entityRefs = useRef([]);
  const savedCameraState = useRef(null);
  const orbitListenerRef = useRef(null);
  const orbitTimeoutRef = useRef(null);

  const { user } = useAuth();
  const generatedBy = user?.fullName || user?.full_name || user?.username || 'System User';

  // ─── Report state ───
  const [showReport, setShowReport] = useState(false);

  // ─── 3D visualization state ───
  const [show3D, setShow3D] = useState(false);
  // Auto-orbit pause: state drives the button icon, ref is read by the per-frame tick listener
  const [orbitPaused, setOrbitPaused] = useState(false);
  const orbitPausedRef = useRef(false);

  const toggleOrbitPause = () => {
    orbitPausedRef.current = !orbitPausedRef.current;
    setOrbitPaused(orbitPausedRef.current);
  };

  // Notify parent when 3D visualization state changes
  useEffect(() => {
    if (onShow3DChange) onShow3DChange(show3D);
  }, [show3D, onShow3DChange]);

  // ─── Phase state ───
  const [phase, setPhase] = useState('idle');
  // idle | uploading | metadata_ready | validating | validated | analyzing | completed | error

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // ─── Upload state ───
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ─── Uploaded images with metadata (from backend) ───
  const [analysisId, setAnalysisId] = useState(null); // DB slanting_analyses.id, created at upload time
  const [uploadedImages, setUploadedImages] = useState([]);
  // Each: { id, filename, s3_key, metadata: { latitude, longitude, gimbal_pitch, flight_yaw, ... }, warnings: [] }

  // ─── Validation state (from Vision API) ───
  const [sessionId, setSessionId] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [excludedImages, setExcludedImages] = useState(new Set());

  // ─── Analysis / Polling state ───
  const [jobId, setJobId] = useState(null);
  const [pollingStage, setPollingStage] = useState(null);
  const [pollingProgress, setPollingProgress] = useState(null);

  // ─── Results ───
  const [analysisData, setAnalysisData] = useState(null);

  // ─── Analysis history ───
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  // Delete-session confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ─── UI state ───
  const [expandedFrame, setExpandedFrame] = useState(null);
  const [frameStageTab, setFrameStageTab] = useState(0);

  // ─── Derived values ───
  const summary = analysisData?.summary;
  const frames = analysisData?.frames || [];
  const band = summary ? getTiltBand(summary.max_tilt_degrees) : null;
  const BandIcon = band?.icon;
  const maxTiltFrame = frames.length > 0
    ? frames.reduce((max, f) => Math.abs(f.tilt_angle_degrees) > Math.abs(max.tilt_angle_degrees) ? f : max, frames[0])
    : null;

  // ─── Fetch existing analysis + history on open ───
  useEffect(() => {
    if (!open || !asset?.id) return;
    const fetchExisting = async () => {
      setLoading(true);
      try {
        // Fetch latest completed analysis + full history in parallel
        const [analysisRes, historyRes] = await Promise.allSettled([
          api.get(`/slanting/analysis/${asset.id}`),
          api.get(`/slanting/history/${asset.id}`)
        ]);

        if (analysisRes.status === 'fulfilled' && analysisRes.value.data) {
          setAnalysisData(analysisRes.value.data);
          setAnalysisId(analysisRes.value.data.analysis_id);
          setPhase('completed');
        } else {
          setAnalysisData(null);
          setPhase('idle');
        }

        if (historyRes.status === 'fulfilled' && historyRes.value.data?.analyses) {
          setAnalysisHistory(historyRes.value.data.analyses);
        }
      } catch (err) {
        setAnalysisData(null);
        setPhase('idle');
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
  }, [open, asset?.id]);

  // ─── Reset on close ───
  useEffect(() => {
    if (!open) {
      setUploadFiles([]);
      setUploadProgress(0);
      setAnalysisId(null);
      setUploadedImages([]);
      setSessionId(null);
      setValidationResult(null);
      setExcludedImages(new Set());
      setJobId(null);
      setPollingStage(null);
      setPollingProgress(null);
      setExpandedFrame(null);
      setFrameStageTab(0);
      setAnalysisHistory([]);
      setHistoryExpanded(false);
      setShow3D(false);
      setErrorMsg(null);
      setSuccessMsg(null);
    }
  }, [open]);

  // ─── Clean up preview URLs ───
  useEffect(() => {
    return () => {
      uploadFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    };
  }, [uploadFiles]);

  // ─── Polling effect ───
  useEffect(() => {
    if (phase !== 'analyzing' || !jobId) return;

    const intervalId = setInterval(async () => {
      try {
        const res = await api.get(`/slanting/jobs/${jobId}/status`);
        const data = res.data;

        if (data.status === 'completed') {
          clearInterval(intervalId);
          // Fetch full result
          try {
            const resultRes = await api.get(`/slanting/jobs/${jobId}/result`);
            setAnalysisData(resultRes.data);
            setAnalysisId(resultRes.data.analysis_id);
            setPhase('completed');
            setSuccessMsg('Slanting analysis completed successfully.');
            refreshHistory(); // Update history list with the new analysis
            if (onDataMutated) onDataMutated();
            setTimeout(() => setSuccessMsg(null), 5000);
          } catch (resultErr) {
            setErrorMsg('Analysis completed but failed to fetch results.');
            setPhase('error');
          }
        } else if (data.status === 'failed') {
          clearInterval(intervalId);
          setErrorMsg(data.message || 'Analysis failed.');
          setPhase('error');
        } else {
          // Still processing
          setPollingStage(data.stage);
          setPollingProgress(data.progress);
        }
      } catch (err) {
        console.error('[Slanting] Polling error:', err);
        clearInterval(intervalId);
        setErrorMsg('Lost connection to analysis service.');
        setPhase('error');
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [phase, jobId, onDataMutated]);

  // ─── 3D visualization: create/remove entities ───
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;

    // Cleanup function
    const stopOrbit = () => {
      if (orbitListenerRef.current) {
        viewer.clock.onTick.removeEventListener(orbitListenerRef.current);
        orbitListenerRef.current = null;
      }
      if (orbitTimeoutRef.current) {
        clearTimeout(orbitTimeoutRef.current);
        orbitTimeoutRef.current = null;
      }
      // Unlock camera after lookAt lock
      viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
    };

    const removeEntities = () => {
      stopOrbit();
      entityRefs.current.forEach(e => {
        if (viewer.entities.contains(e)) viewer.entities.remove(e);
      });
      entityRefs.current = [];
    };

    if (!show3D || !analysisData?.summary) {
      removeEntities();
      // Restore camera
      if (savedCameraState.current) {
        viewer.camera.flyTo({
          destination: savedCameraState.current.position,
          orientation: {
            heading: savedCameraState.current.heading,
            pitch: savedCameraState.current.pitch,
            roll: savedCameraState.current.roll
          },
          duration: 1.5
        });
        savedCameraState.current = null;
      }
      return;
    }

    const lat = parseFloat(asset?.center_lat);
    const lng = parseFloat(asset?.center_lng);
    if (!lat || !lng) return;

    const maxTilt = analysisData.summary.max_tilt_degrees || 0;
    const tiltDir = analysisData.summary.tilt_direction_degrees || 0;
    const tiltBand = getTiltBand(maxTilt);

    const poleHeight = 30;
    const poleRadius = 0.75;

    // Save camera state before flyTo
    savedCameraState.current = {
      position: viewer.camera.position.clone(),
      heading: viewer.camera.heading,
      pitch: viewer.camera.pitch,
      roll: viewer.camera.roll
    };

    // Remove any existing entities first
    removeEntities();

    // A fresh 3D session always starts orbiting (play state)
    orbitPausedRef.current = false;
    setOrbitPaused(false);

    // ── Shared ground base for both poles (bottom anchored at marker position) ──
    const groundPosition = Cesium.Cartesian3.fromDegrees(lng, lat, 0);
    const verticalCenter = Cesium.Cartesian3.fromDegrees(lng, lat, poleHeight / 2);

    // ── Tilted pole: compute center so the bottom circle sits exactly at groundPosition ──
    const hpr = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(tiltDir),
      Cesium.Math.toRadians(maxTilt),
      0
    );

    // Use Cesium's own HPR rotation to get the tilted axis direction in local ENU
    const hprRotation = Cesium.Matrix3.fromHeadingPitchRoll(hpr);
    const tiltedAxisENU = Cesium.Matrix3.multiplyByVector(
      hprRotation,
      new Cesium.Cartesian3(0, 0, 1), // local up = cylinder axis
      new Cesium.Cartesian3()
    );

    // Offset center from ground by half pole height along the tilted axis
    const centerOffsetENU = Cesium.Cartesian3.multiplyByScalar(
      tiltedAxisENU, poleHeight / 2, new Cesium.Cartesian3()
    );
    const enuTransform = Cesium.Transforms.eastNorthUpToFixedFrame(groundPosition);
    const tiltedCenter = Cesium.Matrix4.multiplyByPoint(
      enuTransform, centerOffsetENU, new Cesium.Cartesian3()
    );

    const orientation = Cesium.Transforms.headingPitchRollQuaternion(tiltedCenter, hpr);

    const tiltedPole = viewer.entities.add({
      name: 'Slanting Analysis - Tilted Pole',
      position: tiltedCenter,
      orientation: orientation,
      cylinder: {
        length: poleHeight,
        topRadius: poleRadius * 0.6,
        bottomRadius: poleRadius,
        material: Cesium.Color.fromCssColorString(tiltBand.color).withAlpha(0.85),
        outline: true,
        outlineColor: Cesium.Color.fromCssColorString(tiltBand.color),
        outlineWidth: 2,
      }
    });

    // ── Vertical reference (ghost) cylinder — centered at poleHeight/2 so bottom is at ground ──
    const refPole = viewer.entities.add({
      name: 'Slanting Analysis - Vertical Reference',
      position: verticalCenter,
      cylinder: {
        length: poleHeight,
        topRadius: poleRadius * 0.6,
        bottomRadius: poleRadius,
        material: Cesium.Color.WHITE.withAlpha(0.15),
        outline: true,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.3),
        outlineWidth: 1,
      }
    });

    entityRefs.current = [tiltedPole, refPole];

    // ── Camera flyTo — same pattern as asset marker click (InteractionHandler.js) ──
    const cameraHeading = Cesium.Math.toRadians((tiltDir + 90) % 360);
    const boundingSphere = new Cesium.BoundingSphere(verticalCenter, 1.0);
    const offset = new Cesium.HeadingPitchRange(
      cameraHeading,
      Cesium.Math.toRadians(-35),
      80 // range in meters
    );
    viewer.camera.flyToBoundingSphere(boundingSphere, {
      offset: offset,
      duration: 2.0
    });

    // ── Start slow orbit after flyTo completes ──
    orbitTimeoutRef.current = setTimeout(() => {
      let orbitHeading = cameraHeading;
      const orbitTarget = verticalCenter;
      const orbitPitch = Cesium.Math.toRadians(-35);
      const orbitRange = 80;

      orbitListenerRef.current = () => {
        // Paused: skip the per-frame update so the camera stays frozen at its current view
        if (orbitPausedRef.current) return;
        orbitHeading += Cesium.Math.toRadians(0.15); // ~0.15° per frame ≈ slow orbit
        viewer.camera.lookAt(
          orbitTarget,
          new Cesium.HeadingPitchRange(orbitHeading, orbitPitch, orbitRange)
        );
      };
      viewer.clock.onTick.addEventListener(orbitListenerRef.current);
    }, 2200); // start after flyTo finishes (2s + 200ms buffer)

    return () => removeEntities();
  }, [show3D, analysisData]);

  // ═══════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════

  // ─── Dropzone ───
  const onDrop = useCallback(acceptedFiles => {
    const filesWithPreview = acceptedFiles.map(file =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
    setUploadFiles(prev => {
      const combined = [...prev, ...filesWithPreview];
      return combined.slice(0, 10); // Cap at 10
    });
    setErrorMsg(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png'] },
    maxSize: 20971520, // 20MB
    disabled: phase === 'uploading',
  });

  const removeFile = (index) => {
    setUploadFiles(prev => {
      const updated = [...prev];
      if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  // ─── Upload images to IRIS backend ───
  const handleUpload = async () => {
    if (uploadFiles.length < 4) {
      setErrorMsg('Minimum 4 images required.');
      return;
    }

    setPhase('uploading');
    setUploadProgress(0);
    setErrorMsg(null);

    try {
      const formData = new FormData();
      uploadFiles.forEach(file => formData.append('images', file));

      const response = await api.post(`/slanting/upload/${asset.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          setUploadProgress(Math.round((e.loaded * 100) / e.total));
        }
      });

      if (response.data?.images) {
        setAnalysisId(response.data.analysis_id);
        setUploadedImages(response.data.images);
        setPhase('metadata_ready');
        // Clear file objects (previews no longer needed, we have s3_keys)
        uploadFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
        setUploadFiles([]);
      }
    } catch (err) {
      console.error('[Slanting] Upload error:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to upload images.');
      setPhase('idle');
    }
  };

  // ─── Validate with Vision API ───
  const handleValidate = async () => {
    setPhase('validating');
    setErrorMsg(null);

    try {
      const imageMetadata = uploadedImages.map(img => ({
        filename: img.filename,
        s3_key: img.s3_key,
        ...img.metadata
      }));

      const res = await api.post(`/slanting/validate/${asset.id}`, {
        analysis_id: analysisId,
        image_metadata: imageMetadata
      });

      setSessionId(res.data.session_id);
      setValidationResult(res.data);

      // Auto-exclude images with pole_detected: false
      const autoExclude = new Set();
      (res.data.per_image || []).forEach(img => {
        if (!img.pole_detected) autoExclude.add(img.filename);
      });
      setExcludedImages(autoExclude);

      setPhase('validated');
    } catch (err) {
      console.error('[Slanting] Validate error:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to validate images.');
      setPhase('metadata_ready');
    }
  };

  // ─── Toggle image exclusion ───
  const toggleExclude = (filename) => {
    setExcludedImages(prev => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const includedCount = uploadedImages.length - excludedImages.size;

  // ─── Confirm analysis ───
  const handleConfirmAnalysis = async () => {
    if (includedCount < 4) {
      setErrorMsg('At least 4 images must remain after exclusions.');
      return;
    }

    setPhase('analyzing');
    setPollingStage(null);
    setPollingProgress(null);
    setErrorMsg(null);

    try {
      const imageMetadata = uploadedImages
        .filter(img => !excludedImages.has(img.filename))
        .map(img => ({ filename: img.filename, s3_key: img.s3_key, ...img.metadata }));

      const res = await api.post(`/slanting/analyze/${asset.id}`, {
        analysis_id: analysisId,
        session_id: sessionId,
        exclude_images: [...excludedImages],
        image_metadata: imageMetadata
      });

      setJobId(res.data.job_id);
    } catch (err) {
      console.error('[Slanting] Analyze error:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to start analysis.');
      setPhase('validated');
    }
  };

  // ─── Re-analyze (reset to upload) ───
  const handleReAnalyze = () => {
    setAnalysisData(null);
    setAnalysisId(null);
    setUploadedImages([]);
    setSessionId(null);
    setValidationResult(null);
    setExcludedImages(new Set());
    setJobId(null);
    setPollingStage(null);
    setPollingProgress(null);
    setPhase('idle');
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  const handleFrameClick = (frameIndex) => {
    setExpandedFrame(expandedFrame === frameIndex ? null : frameIndex);
    setFrameStageTab(0);
  };

  // ─── Load a specific analysis from history ───
  const handleLoadAnalysis = async (historyAnalysisId) => {
    if (historyAnalysisId === analysisId) return; // already loaded
    setLoading(true);
    setExpandedFrame(null);
    try {
      const res = await api.get(`/slanting/analysis-by-id/${historyAnalysisId}`);
      if (res.data) {
        setAnalysisData(res.data);
        setAnalysisId(historyAnalysisId);
        setPhase('completed');
      }
    } catch (err) {
      console.error('[Slanting] Error loading analysis:', err);
      setErrorMsg('Failed to load analysis.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Refresh history list (called after new analysis completes) ───
  const refreshHistory = async () => {
    try {
      const res = await api.get(`/slanting/history/${asset.id}`);
      if (res.data?.analyses) setAnalysisHistory(res.data.analyses);
    } catch (err) {
      console.error('[Slanting] Error refreshing history:', err);
    }
  };

  // ─── Delete an analysis session (hard delete; photos cascade in DB) ───
  const handleDeleteAnalysis = async () => {
    const targetId = deleteConfirmId;
    if (!targetId) return;
    setDeleting(true);
    try {
      await api.delete(`/slanting/analysis-by-id/${targetId}`);

      // Pull the fresh history so we can decide what to show next
      let updated = [];
      try {
        const res = await api.get(`/slanting/history/${asset.id}`);
        updated = res.data?.analyses || [];
        setAnalysisHistory(updated);
      } catch (e) {
        console.error('[Slanting] Error refreshing history after delete:', e);
      }

      // If we deleted the session currently on screen, fall back to the latest
      // remaining completed one, or reset to the upload state if none remain.
      if (targetId === analysisId) {
        const latestCompleted = updated.find(a => a.status === 'completed');
        if (latestCompleted) {
          await handleLoadAnalysis(latestCompleted.id);
        } else {
          setShow3D(false);
          setAnalysisData(null);
          setAnalysisId(null);
          setPhase('idle');
        }
      }

      setSuccessMsg('Analysis session deleted.');
      if (onDataMutated) onDataMutated();
    } catch (err) {
      console.error('[Slanting] Error deleting analysis:', err);
      setErrorMsg('Failed to delete analysis session.');
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════

  const renderWarningLine = (warning, index) => {
    const cfg = SEVERITY_CONFIG[warning.severity] || SEVERITY_CONFIG.info;
    const Icon = cfg.icon;
    return (
      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, lineHeight: 1.3 }}>
        <Icon sx={{ fontSize: '0.75rem', color: cfg.color, mt: '2px', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: cfg.color, fontSize: '0.7rem' }}>
          {warning.message}
        </Typography>
      </Box>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={show3D ? false : 'md'}
      fullWidth={!show3D}
      slotProps={{
        backdrop: { sx: show3D ? { backgroundColor: 'transparent', pointerEvents: 'none' } : {} }
      }}
      PaperProps={{
        sx: show3D
          ? {
              position: 'fixed', left: 0, top: 0, bottom: 0, margin: 0,
              width: '470px', maxWidth: '470px', maxHeight: '100vh', height: '100vh',
              borderRadius: '0 12px 12px 0', transition: 'all 0.3s ease-in-out',
            }
          : { borderRadius: 3, maxHeight: '90vh' }
      }}
    >

      {/* ─── Header ─── */}
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, py: 1.5, px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <StraightenIcon sx={{ color: DEEP_PURPLE, transform: 'rotate(90deg)', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Slanting Analysis
            </Typography>
            {asset && (
              <Typography variant="caption" color="text.secondary" noWrap title={`${asset.name || asset.code} ${asset.code ? `(${asset.code})` : ''}`} sx={{ display: 'block' }}>
                {asset.name || asset.code} {asset.code ? `(${asset.code})` : ''}
              </Typography>
            )}
          </Box>
          {phase === 'completed' && (
            <Chip label="Completed" size="small" icon={<CheckCircleIcon sx={{ fontSize: '0.9rem' }} />}
              sx={{ ml: 1, flexShrink: 0, bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', fontWeight: 600 }} />
          )}
          {phase === 'analyzing' && (
            <Chip label="Processing" size="small"
              sx={{ ml: 1, flexShrink: 0, bgcolor: alpha(DEEP_PURPLE, 0.1), color: DEEP_PURPLE, fontWeight: 600 }} />
          )}
          {(phase === 'validated' || phase === 'metadata_ready') && (
            <Chip label="Review" size="small"
              sx={{ ml: 1, flexShrink: 0, bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', fontWeight: 600 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {/* 3D Visualization Toggle */}
          {phase === 'completed' && analysisData && asset?.center_lat && (
            <Tooltip title={
              !analysisData.summary?.tilt_direction_degrees && analysisData.summary?.tilt_direction_degrees !== 0
                ? 'Tilt direction unavailable — requires heading data'
                : show3D ? 'Exit 3D View' : '3D Pole Visualization'
            }>
              <span>
                <IconButton
                  onClick={() => setShow3D(prev => !prev)}
                  size="small"
                  disabled={!analysisData.summary?.tilt_direction_degrees && analysisData.summary?.tilt_direction_degrees !== 0}
                  sx={show3D ? { bgcolor: alpha(DEEP_PURPLE, 0.15), color: DEEP_PURPLE } : {}}
                >
                  <ThreeDRotationIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {/* Pause / resume the auto-orbit while in 3D view */}
          {show3D && (
            <Tooltip title={orbitPaused ? 'Resume rotation' : 'Pause rotation'}>
              <IconButton onClick={toggleOrbitPause} size="small" sx={{ color: DEEP_PURPLE }}>
                {orbitPaused ? <PlayArrowIcon /> : <PauseIcon />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2, '&.MuiDialogContent-root': { paddingTop: '24px' } }}>
        {/* Status messages */}
        {errorMsg && <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ mb: 2 }}>{errorMsg}</Alert>}
        {successMsg && <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>{successMsg}</Alert>}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: DEEP_PURPLE }} />
          </Box>
        )}

        {!loading && (
          <>
            {/* ═══════ COMPLETED: Assessment Summary ═══════ */}
            {phase === 'completed' && analysisData && band && (
              <Paper elevation={0} sx={{
                p: 2.5, mb: 3, borderRadius: 2,
                borderLeft: `4px solid ${band.color}`,
                bgcolor: alpha(band.color, 0.04),
                border: `1px solid ${alpha(band.color, 0.15)}`, borderLeftWidth: 4
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {BandIcon && <BandIcon sx={{ color: band.color, fontSize: '1.2rem' }} />}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Assessment Summary</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Max Tilt</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: band.color }}>
                      {summary.max_tilt_degrees.toFixed(1)}°
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Direction</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {summary.tilt_direction_compass ? (
                        <>
                          <NavigationIcon sx={{
                            fontSize: '1.2rem', color: DEEP_PURPLE,
                            transform: `rotate(${summary.tilt_direction_degrees}deg)`
                          }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {summary.tilt_direction_compass}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A (no heading data)</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Classification</Typography>
                    <Chip label={summary.classification_label} size="small"
                      sx={{ mt: 0.5, bgcolor: alpha(band.color, 0.15), color: band.color, fontWeight: 600, fontSize: '0.7rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Confidence</Typography>
                    <Chip label={summary.confidence} size="small"
                      sx={{ mt: 0.5, bgcolor: alpha(DEEP_PURPLE, 0.1), color: DEEP_PURPLE, fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                  </Box>
                </Box>
              </Paper>
            )}

            {/* ═══════ COMPLETED: Analysis History ═══════ */}
            {phase === 'completed' && analysisHistory.length >= 1 && (
              <Accordion
                expanded={historyExpanded}
                onChange={() => setHistoryExpanded(!historyExpanded)}
                elevation={0}
                sx={{
                  mb: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0, mb: 3 }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Analysis History ({analysisHistory.length} {analysisHistory.length === 1 ? 'session' : 'sessions'})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                  <List dense disablePadding>
                    {analysisHistory.map((entry) => {
                      const isCurrent = entry.id === analysisId;
                      const isCompleted = entry.status === 'completed';
                      const entryBand = isCompleted && entry.max_tilt_degrees != null
                        ? getTiltBand(entry.max_tilt_degrees) : null;
                      const dotColor = entryBand ? entryBand.color
                        : entry.status === 'failed' ? '#f44336' : theme.palette.grey[400];

                      const statusConfig = {
                        completed: { label: 'Completed', color: '#4caf50' },
                        analyzing: { label: 'Analyzing', color: DEEP_PURPLE },
                        validated: { label: 'Validated', color: '#ff9800' },
                        validating: { label: 'Validating', color: '#ff9800' },
                        draft: { label: 'Draft', color: theme.palette.grey[500] },
                        failed: { label: 'Failed', color: '#f44336' },
                      };
                      const sc = statusConfig[entry.status] || statusConfig.draft;

                      return (
                        <ListItem
                          key={entry.id}
                          onClick={() => isCompleted && handleLoadAnalysis(entry.id)}
                          sx={{
                            borderRadius: 1.5, mb: 0.5, py: 0.8, px: 1.5,
                            cursor: isCompleted ? 'pointer' : 'default',
                            opacity: isCompleted ? 1 : 0.5,
                            bgcolor: isCurrent ? alpha(DEEP_PURPLE, 0.06) : 'transparent',
                            borderLeft: isCurrent ? `3px solid ${DEEP_PURPLE}` : '3px solid transparent',
                            transition: 'all 0.15s',
                            '&:hover': isCompleted ? { bgcolor: alpha(DEEP_PURPLE, 0.04) } : {}
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 10, color: dotColor }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: isCurrent ? 700 : 500, fontSize: '0.8rem' }}>
                                  #{entry.id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {entry.completed_at
                                    ? new Date(entry.completed_at).toLocaleString()
                                    : new Date(entry.created_at).toLocaleString()}
                                </Typography>
                                <Chip label={sc.label} size="small" sx={{
                                  height: 18, fontSize: '0.6rem', fontWeight: 600,
                                  bgcolor: alpha(sc.color, 0.1), color: sc.color
                                }} />
                                {isCompleted && entry.max_tilt_degrees != null && (
                                  <>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: entryBand?.color, fontSize: '0.75rem' }}>
                                      {entry.max_tilt_degrees.toFixed(1)}°
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {entry.classification_label}
                                    </Typography>
                                  </>
                                )}
                                {entry.image_count && (
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                                    {entry.image_count} img
                                  </Typography>
                                )}
                                {isCurrent && (
                                  <Chip label="Current" size="small" sx={{
                                    height: 16, fontSize: '0.55rem', fontWeight: 700,
                                    bgcolor: alpha(DEEP_PURPLE, 0.15), color: DEEP_PURPLE
                                  }} />
                                )}
                              </Box>
                            }
                          />
                          <Tooltip title="Delete session">
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(entry.id); }}
                              sx={{
                                ml: 0.5, flexShrink: 0, color: alpha('#f44336', 0.6),
                                '&:hover': { color: '#f44336', bgcolor: alpha('#f44336', 0.08) }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* ═══════ IDLE / COMPLETED: Upload Dropzone ═══════ */}
            {(phase === 'idle' || phase === 'completed') && (
              <Paper elevation={0} sx={{
                p: 3, mb: 3, borderRadius: 2,
                border: `2px dashed ${isDragActive ? DEEP_PURPLE : alpha(theme.palette.divider, 0.3)}`,
                bgcolor: isDragActive ? alpha(DEEP_PURPLE, 0.04) : 'transparent',
                textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'
              }} {...getRootProps()}>
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 40, color: alpha(DEEP_PURPLE, 0.5), mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {phase === 'completed' ? 'Upload New Orbit Images (Re-analyze)' : 'Upload Orbit Images'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Drag & drop or click to select — 4 to 10 JPEG/PNG images, max 20MB each
                </Typography>
              </Paper>
            )}

            {/* Selected files preview (before upload) */}
            {(phase === 'idle' || phase === 'completed') && uploadFiles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Selected Images ({uploadFiles.length}/10)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
                  {uploadFiles.map((file, i) => (
                    <Box key={i} sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.15)}` }}>
                      <Box component="img" src={file.preview} sx={{ width: '100%', height: 80, objectFit: 'cover' }} />
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                        sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', width: 20, height: 20, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Typography variant="caption" sx={{ display: 'block', px: 0.5, py: 0.3, fontSize: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button variant="contained" onClick={handleUpload}
                  disabled={uploadFiles.length < 4}
                  startIcon={<CloudUploadIcon />}
                  sx={{ bgcolor: DEEP_PURPLE, borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.85) } }}>
                  Upload & Extract Metadata ({uploadFiles.length} images)
                </Button>
                {uploadFiles.length < 4 && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    Minimum 4 images required ({4 - uploadFiles.length} more needed)
                  </Typography>
                )}
              </Box>
            )}

            {/* ═══════ UPLOADING: Progress ═══════ */}
            {phase === 'uploading' && (
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha(DEEP_PURPLE, 0.15)}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <CircularProgress size={20} sx={{ color: DEEP_PURPLE }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Uploading {uploadFiles.length} images and extracting metadata...
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={uploadProgress}
                  sx={{ height: 8, borderRadius: 4, bgcolor: alpha(DEEP_PURPLE, 0.1), '& .MuiLinearProgress-bar': { bgcolor: DEEP_PURPLE, borderRadius: 4 } }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                  {uploadProgress}%
                </Typography>
              </Paper>
            )}

            {/* ═══════ METADATA READY: Show uploaded images + IRIS warnings ═══════ */}
            {phase === 'metadata_ready' && uploadedImages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Uploaded Images — Metadata Review ({uploadedImages.length} images)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
                  {uploadedImages.map((img, i) => (
                    <Paper key={i} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Box sx={{ width: 80, height: 60, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: alpha(theme.palette.grey[500], 0.08) }}>
                          {img.s3_key ? (
                            <AuthenticatedImage src={img.s3_key} alt={img.filename} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                              <ImageIcon sx={{ color: 'text.disabled' }} />
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {img.filename}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {img.metadata?.is_drone_photo && <Chip label="Drone" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(DEEP_PURPLE, 0.1), color: DEEP_PURPLE }} />}
                            {img.metadata?.latitude && <Chip label="GPS" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha('#4caf50', 0.1), color: '#4caf50' }} />}
                            {img.metadata?.gimbal_pitch !== null && <Chip label={`Gimbal: ${img.metadata.gimbal_pitch?.toFixed(1)}°`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
                            {img.metadata?.flight_yaw !== null && <Chip label={`Heading: ${img.metadata.flight_yaw?.toFixed(0)}°`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
                          </Box>
                          {img.warnings?.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
                              {img.warnings.map((w, wi) => renderWarningLine(w, wi))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
                <Button variant="contained" onClick={handleValidate}
                  startIcon={<VerifiedIcon />}
                  sx={{ bgcolor: DEEP_PURPLE, borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.85) } }}>
                  Validate with AI (Pole Detection)
                </Button>
              </Box>
            )}

            {/* ═══════ VALIDATING: Spinner ═══════ */}
            {phase === 'validating' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                <CircularProgress sx={{ color: DEEP_PURPLE }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Validating images with AI...</Typography>
                <Typography variant="caption" color="text.secondary">Checking pole detection and image quality</Typography>
              </Box>
            )}

            {/* ═══════ VALIDATED: Review warnings + exclude + confirm ═══════ */}
            {phase === 'validated' && validationResult && (
              <Box sx={{ mb: 3 }}>
                {/* Assessment summary */}
                <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: alpha(DEEP_PURPLE, 0.03), border: `1px solid ${alpha(DEEP_PURPLE, 0.1)}` }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>AI Validation Summary</Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Pole Detection</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{validationResult.assessment?.pole_detection_rate}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Angular Coverage</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{validationResult.assessment?.estimated_angular_coverage_degrees}°</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Quality</Typography>
                      <Chip label={validationResult.assessment?.overall_quality} size="small"
                        sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                    </Box>
                  </Box>
                </Paper>

                {/* Per-image review with exclude checkboxes */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Image Review — Uncheck to exclude ({includedCount} of {uploadedImages.length} included)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 2 }}>
                  {uploadedImages.map((img, i) => {
                    const apiResult = (validationResult.per_image || []).find(p => p.filename === img.filename) || {};
                    const isExcluded = excludedImages.has(img.filename);
                    const allWarnings = [...(img.warnings || []), ...(apiResult.warnings || [])];

                    return (
                      <Paper key={i} elevation={0} sx={{
                        p: 1.5, borderRadius: 2,
                        border: `1px solid ${isExcluded ? alpha('#f44336', 0.2) : alpha(theme.palette.divider, 0.1)}`,
                        opacity: isExcluded ? 0.5 : 1, transition: 'all 0.2s'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={!isExcluded}
                            onChange={() => toggleExclude(img.filename)}
                            size="small"
                            sx={{ color: DEEP_PURPLE, '&.Mui-checked': { color: DEEP_PURPLE } }}
                          />
                          <Box sx={{ width: 50, height: 40, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: alpha(theme.palette.grey[500], 0.08) }}>
                            {img.s3_key && <AuthenticatedImage src={img.s3_key} alt={img.filename} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{img.filename}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                              {apiResult.pole_detected !== undefined && (
                                <Chip
                                  icon={apiResult.pole_detected ? <CheckCircleIcon sx={{ fontSize: '0.7rem' }} /> : <ErrorIcon sx={{ fontSize: '0.7rem' }} />}
                                  label={apiResult.pole_detected ? `Pole: ${(apiResult.pole_confidence * 100).toFixed(0)}%` : 'No Pole'}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: '0.6rem',
                                    bgcolor: alpha(apiResult.pole_detected ? '#4caf50' : '#f44336', 0.1),
                                    color: apiResult.pole_detected ? '#4caf50' : '#f44336'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        {allWarnings.length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5, ml: 5 }}>
                            {allWarnings.map((w, wi) => renderWarningLine(w, wi))}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button variant="contained" onClick={handleConfirmAnalysis}
                    disabled={includedCount < 4 || !validationResult?.can_proceed}
                    startIcon={<PlayArrowIcon />}
                    sx={{ bgcolor: DEEP_PURPLE, borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.85) } }}>
                    Confirm Analysis ({includedCount} images)
                  </Button>
                  <Button variant="outlined" onClick={handleReAnalyze}
                    sx={{ textTransform: 'none', borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.3) }}>
                    Re-upload
                  </Button>
                  {includedCount < 4 && (
                    <Typography variant="caption" color="error">At least 4 images required</Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* ═══════ ANALYZING: Processing progress ═══════ */}
            {phase === 'analyzing' && (
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha(DEEP_PURPLE, 0.15)}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <CircularProgress size={20} sx={{ color: DEEP_PURPLE }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {pollingStage ? pollingStage.label : PROCESSING_STAGES[0].label}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pollingProgress?.percent_overall || 0}
                  sx={{ height: 8, borderRadius: 4, bgcolor: alpha(DEEP_PURPLE, 0.1), '& .MuiLinearProgress-bar': { bgcolor: DEEP_PURPLE, borderRadius: 4 } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Stage {(pollingStage?.index || 0) + 1} of {PROCESSING_STAGES.length}
                    {pollingProgress?.current_filename && ` — ${pollingProgress.current_filename}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pollingProgress?.percent_overall || 0}%
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* ═══════ ERROR: Retry ═══════ */}
            {phase === 'error' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ErrorIcon sx={{ fontSize: 48, color: '#f44336', mb: 2 }} />
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                  {errorMsg || 'An error occurred during analysis.'}
                </Typography>
                <Button variant="outlined" onClick={handleReAnalyze} startIcon={<RefreshIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2 }}>
                  Try Again
                </Button>
              </Box>
            )}

            {/* ═══════ COMPLETED: Frame Gallery ═══════ */}
            {phase === 'completed' && frames.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Frame Analysis ({frames.length} Frames)
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                  gap: 1.5
                }}>
                  {frames.map((frame) => {
                    const frameBand = getTiltBand(frame.tilt_angle_degrees);
                    const isMax = maxTiltFrame && frame.image_index === maxTiltFrame.image_index;
                    const isExpanded = expandedFrame === frame.image_index;
                    return (
                      <Paper key={frame.image_index} elevation={0}
                        onClick={() => handleFrameClick(frame.image_index)}
                        sx={{
                          p: 1.5, borderRadius: 2, cursor: 'pointer',
                          border: `2px solid ${isMax ? frameBand.color : isExpanded ? DEEP_PURPLE : alpha(theme.palette.divider, 0.15)}`,
                          bgcolor: isExpanded ? alpha(DEEP_PURPLE, 0.04) : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.04), borderColor: alpha(DEEP_PURPLE, 0.3) }
                        }}>
                        {/* Thumbnail */}
                        <Box sx={{ height: 80, borderRadius: 1, mb: 1, overflow: 'hidden', position: 'relative', bgcolor: alpha(theme.palette.grey[500], 0.08) }}>
                          {frame.s3_key ? (
                            <AuthenticatedImage src={frame.s3_key} alt={frame.filename}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                              <CameraAltIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                                {frame.viewing_angle_degrees}°
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', color: frameBand.color }}>
                          {frame.tilt_angle_degrees.toFixed(1)}°
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', fontSize: '0.65rem' }}>
                          View: {frame.viewing_angle_degrees}°
                        </Typography>
                        {isMax && (
                          <Chip label="MAX" size="small" sx={{
                            mt: 0.5, width: '100%', height: 18,
                            bgcolor: alpha(frameBand.color, 0.15), color: frameBand.color,
                            fontWeight: 700, fontSize: '0.6rem'
                          }} />
                        )}
                      </Paper>
                    );
                  })}
                </Box>

                {/* Expanded Frame Detail with annotation overlay */}
                <Collapse in={expandedFrame !== null} timeout={300}>
                  {expandedFrame !== null && frames[expandedFrame] && (
                    <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: `1px solid ${alpha(DEEP_PURPLE, 0.15)}` }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Frame {expandedFrame + 1} — Viewing Angle: {frames[expandedFrame].viewing_angle_degrees}°
                        {frames[expandedFrame].heading_compass && ` (${frames[expandedFrame].heading_compass})`}
                      </Typography>
                      <Tabs value={frameStageTab} onChange={(_, v) => setFrameStageTab(v)}
                        variant="scrollable" scrollButtons="auto"
                        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}>
                        {FRAME_STAGES.map((stage) => (
                          <Tab key={stage.key} label={stage.label} />
                        ))}
                      </Tabs>
                      {/* Image with SVG overlay */}
                      <Box sx={{
                        position: 'relative', borderRadius: 2, overflow: 'hidden',
                        bgcolor: alpha(theme.palette.grey[500], 0.06),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }}>
                        {frames[expandedFrame].s3_key ? (
                          <>
                            <AuthenticatedImage
                              src={frames[expandedFrame].s3_key}
                              alt={`Frame ${expandedFrame + 1}`}
                              sx={{ width: '100%', display: 'block' }}
                            />
                            {FRAME_STAGES[frameStageTab]?.key !== 'raw' && frames[expandedFrame].annotations && (
                              <AnnotationOverlay
                                annotations={frames[expandedFrame].annotations}
                                stageKey={FRAME_STAGES[frameStageTab]?.key}
                              />
                            )}
                          </>
                        ) : (
                          <Box sx={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <CameraAltIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">{FRAME_STAGES[frameStageTab]?.label}</Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 3, mt: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          Tilt: <strong>{frames[expandedFrame].tilt_angle_degrees.toFixed(1)}°</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Gimbal Pitch: <strong>{frames[expandedFrame].gimbal_pitch_degrees.toFixed(1)}°</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Confidence: <strong>{(frames[expandedFrame].confidence_score * 100).toFixed(0)}%</strong>
                        </Typography>
                        {frames[expandedFrame].heading_degrees !== null && (
                          <Typography variant="caption" color="text.secondary">
                            Heading: <strong>{frames[expandedFrame].heading_degrees.toFixed(0)}° ({frames[expandedFrame].heading_compass})</strong>
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Collapse>
              </Box>
            )}

            {/* ═══════ COMPLETED: Measurements Table ═══════ */}
            {phase === 'completed' && frames.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Tilt Measurements</Typography>
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(DEEP_PURPLE, 0.04) }}>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Frame</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Viewing Angle</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Heading</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Tilt Angle</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Gimbal Pitch</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Confidence</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {frames.map((frame) => {
                        const frameBand = getTiltBand(frame.tilt_angle_degrees);
                        const isMax = maxTiltFrame && frame.image_index === maxTiltFrame.image_index;
                        return (
                          <TableRow key={frame.image_index} sx={{
                            bgcolor: isMax ? alpha(frameBand.color, 0.06) : 'transparent',
                            '& .MuiTableCell-root': { py: 1, borderColor: alpha(theme.palette.divider, 0.06) }
                          }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: isMax ? 700 : 400 }}>#{frame.image_index + 1}</Typography>
                            </TableCell>
                            <TableCell><Typography variant="body2">{frame.viewing_angle_degrees}°</Typography></TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {frame.heading_degrees !== null ? `${frame.heading_degrees.toFixed(0)}° ${frame.heading_compass || ''}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: frameBand.color }}>
                                {frame.tilt_angle_degrees.toFixed(1)}°
                              </Typography>
                            </TableCell>
                            <TableCell><Typography variant="body2">{frame.gimbal_pitch_degrees.toFixed(1)}°</Typography></TableCell>
                            <TableCell><Typography variant="body2">{(frame.confidence_score * 100).toFixed(0)}%</Typography></TableCell>
                            <TableCell>
                              {isMax ? (
                                <Chip label="MAX TILT" size="small" sx={{ height: 20, bgcolor: alpha(frameBand.color, 0.15), color: frameBand.color, fontWeight: 700, fontSize: '0.6rem' }} />
                              ) : (
                                <Chip label={frameBand.label.split(' ')[0]} size="small" sx={{ height: 20, bgcolor: alpha(frameBand.color, 0.1), color: frameBand.color, fontSize: '0.6rem' }} />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* ═══════ Methodology Reference (always visible) ═══════ */}
            <Accordion elevation={0} sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, borderRadius: '12px !important',
              '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 }
            }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MenuBookIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Methodology Reference</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Tilt Interpretation Thresholds
                </Typography>
                <TableContainer sx={{ mb: 2, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                        <TableCell sx={{ fontWeight: 600, py: 0.8, fontSize: '0.75rem' }}>Tilt Range</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.8, fontSize: '0.75rem' }}>Interpretation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {TILT_BANDS.map((b) => (
                        <TableRow key={b.label}>
                          <TableCell sx={{ py: 0.6 }}>
                            <Typography variant="caption">
                              {b.max === Infinity ? `> ${b.min}°` : `${b.min}° — ${b.max}°`}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Chip label={b.label} size="small"
                              sx={{ height: 20, bgcolor: alpha(b.color, 0.12), color: b.color, fontWeight: 600, fontSize: '0.65rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  How to Read Multi-Angle Results
                </Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    1. The <strong>largest tilt value</strong> across all frames is the best estimate of the pole's actual lean.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    2. The <strong>viewing angle that produced the max reading</strong> indicates the lean direction (the pole leans perpendicular to that view).
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    3. Frames showing <strong>near-zero tilt</strong> mean the pole is leaning directly toward/away from those positions.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    4. <strong>Do not average all values</strong> — the maximum is the most meaningful single number.
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Field Capture Requirements
                </Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    - Gimbal pitch must be <strong>0° (horizontal)</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    - Shaft should <strong>fill the frame vertically</strong> (close-up preferred)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    - Capture <strong>10 evenly-spaced photos</strong> around a 360° orbit
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    - Capture during <strong>calm weather</strong> (no wind sway)
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </DialogContent>

      {/* ─── Footer Actions ─── */}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        {phase === 'completed' && (
          <Button onClick={handleReAnalyze} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Re-analyze
          </Button>
        )}
        <Button onClick={onClose} variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          Close
        </Button>
        {phase === 'completed' && analysisData && (
          <Button variant="contained" startIcon={<AssessmentIcon />} onClick={() => setShowReport(true)}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: DEEP_PURPLE, '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.85) } }}>
            Export Report
          </Button>
        )}
      </DialogActions>

      <SlantingReport
        open={showReport}
        onClose={() => setShowReport(false)}
        asset={asset}
        analysisData={analysisData}
        generatedBy={generatedBy}
      />

      {/* Delete-session confirmation */}
      <Dialog open={deleteConfirmId != null} onClose={() => !deleting && setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Analysis Session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently deletes session <strong>#{deleteConfirmId}</strong> and its analyzed frames.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAnalysis}
            disabled={deleting}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SlantingAnalysisModal;
